import React from 'react'
import { CircularProgress, Grid, IconButton, Tooltip, Typography } from '@material-ui/core'
import styled from 'styled-components'
// @ts-ignore
import { EditableCheckbox } from '../../../../../components/common/EditableComponents'
import {
  EAutoOnboardingStepStatus,
  ImportCollectionStatus,
  MigrationCollections,
  MigrationStatus,
} from '../../../../../types/migration'
import {
  formatImportTimestamp,
  getImportStatusPresentation,
  hasImportDownload,
  shouldEnableImportDetails,
} from '../importStatusUtils'
import InfoIcon from '@material-ui/icons/Info'
import AssignmentIcon from '@material-ui/icons/Assignment'
import WarningAmberIcon from '@material-ui/icons/Warning'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import ErrorIcon from '@material-ui/icons/Error'

const GridRow = styled(Grid)({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  minHeight: 70,
  width: '100%',
})

const ExportStatusHeaderGrid = styled(Grid)`
  @media (max-width: 599px) {
    display: none !important;
  }
`

export const CollectionHeaderRow: React.FC = () => {
  return (
    <GridRow container spacing={16} style={{ padding: '8px 0' }}>
      <Grid item xs={6} sm={4}>
        <HeaderText>Collection</HeaderText>
      </Grid>
      <ExportStatusHeaderGrid item xs={12} sm={4}>
        <HeaderText>Export Status</HeaderText>
      </ExportStatusHeaderGrid>
      <Grid item xs={6} sm={4}>
        <HeaderText>Import Status</HeaderText>
      </Grid>
    </GridRow>
  )
}

interface CollectionRowProps {
  migrationStatus: MigrationStatus
  collectionName: MigrationCollections
  isCollectionSelected: boolean
  toggleSelected: () => void
  onDetailsModalOpen: (collectionName: MigrationCollections) => void
  migrationRequestsMap?: Map<MigrationCollections, any[]>
  disabled?: boolean
  importStatus?: ImportCollectionStatus
  onImportDetailsOpen?: (collectionName: MigrationCollections) => void
  isImportAvailable?: boolean
  isImportStatusLoading?: boolean
}

export const CollectionRow: React.FC<CollectionRowProps> = ({
  collectionName,
  isCollectionSelected,
  toggleSelected,
  disabled = false,
  onDetailsModalOpen,
  importStatus,
  onImportDetailsOpen,
  isImportAvailable = false,
  isImportStatusLoading = false,
}) => {
  const isNonImportableCollection =
    collectionName === MigrationCollections.Shops ||
    collectionName === MigrationCollections.Employees

  const importPresentation = getImportStatusPresentation(importStatus?.status)
  const importUpdatedAt = formatImportTimestamp(importStatus?.updatedAt)
  const statusValue = importStatus?.status

  const canShowImport = isImportAvailable && !isNonImportableCollection
  const showImportIcon = canShowImport && statusValue !== EAutoOnboardingStepStatus.NOT_STARTED
  const showUpdatedAt = canShowImport && importUpdatedAt && showImportIcon

  const fileContainsErrors = importStatus?.fileEmpty !== undefined && !importStatus.fileEmpty
  const hasFailed =
    statusValue === EAutoOnboardingStepStatus.FAILED ||
    statusValue === EAutoOnboardingStepStatus.CANCELLED
  const canViewDetails = shouldEnableImportDetails(importStatus) && Boolean(onImportDetailsOpen)
  const isClickableIcon = canShowImport && canViewDetails && (fileContainsErrors || hasFailed)

  // Tooltip text
  const getTooltipText = () => {
    if (!isImportAvailable) return 'Import not available'
    if (!importStatus) return 'No import data yet'
    if (!canViewDetails) {
      if (statusValue === EAutoOnboardingStepStatus.NOT_STARTED) return 'Import not started'
      if (hasImportDownload(importStatus) && !onImportDetailsOpen) return 'Download available'
      return `Import status: ${importPresentation.label}`
    }
    if (statusValue === EAutoOnboardingStepStatus.FAILED) return 'View import errors'
    if (hasImportDownload(importStatus)) return 'View import results'
    return 'View import details'
  }

  const renderStatusIcon = () => {
    if (fileContainsErrors) {
      return <WarningAmberIcon style={{ color: '#ff9800' }} />
    }

    switch (importPresentation.status) {
      case EAutoOnboardingStepStatus.SUCCEEDED:
        return <CheckCircleIcon style={{ color: importPresentation.color }} />
      case EAutoOnboardingStepStatus.FAILED:
      case EAutoOnboardingStepStatus.CANCELLED:
        return <ErrorIcon style={{ color: importPresentation.color }} />
      case EAutoOnboardingStepStatus.IN_PROGRESS:
        return <CircularProgress size={12} className="spinner" />
      case EAutoOnboardingStepStatus.NOT_STARTED:
      default:
        return <AssignmentIcon style={{ color: importPresentation.color }} />
    }
  }

  return (
    <GridRow container spacing={16}>
      <Grid item xs={12} sm={4} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div
          style={{
            alignItems: 'center',
            display: 'flex',
            gap: 10,
            flexDirection: 'row',
            paddingLeft: isNonImportableCollection ? 24 : undefined,
            width: '100%',
          }}
        >
          <EditableCheckbox
            checked={isCollectionSelected}
            disabled={isNonImportableCollection || disabled}
            onChange={toggleSelected}
            valueToRender={'Enabled'}
            editMode={true}
          />
          <StyledTypography disabled={disabled || isNonImportableCollection}>
            {collectionName}
          </StyledTypography>
        </div>
      </Grid>
      <Grid item xs={12} sm={4}>
        <MobileLabel>Export Status</MobileLabel>
        {!isNonImportableCollection ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <InfoIcon
              style={{
                cursor: disabled ? 'default' : 'pointer',
                opacity: disabled ? 0.5 : 1,
              }}
              color={disabled ? 'disabled' : 'primary'}
              onClick={() => {
                if (!disabled) {
                  onDetailsModalOpen(collectionName)
                }
              }}
            />
          </div>
        ) : (
          <span style={{ color: '#9e9e9e' }}></span>
        )}
      </Grid>
      <Grid item xs={12} sm={4}>
        <MobileLabel>Import Status</MobileLabel>
        {isImportStatusLoading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <CircularProgress size={12} className="spinner" />
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {showImportIcon ? (
              <Tooltip title={getTooltipText()}>
                <span>
                  <IconButton
                    onClick={() => {
                      if (isClickableIcon && onImportDetailsOpen) {
                        onImportDetailsOpen(collectionName)
                      }
                    }}
                    disabled={!isClickableIcon}
                    aria-label={`View ${collectionName} import status`}
                  >
                    {renderStatusIcon()}
                  </IconButton>
                </span>
              </Tooltip>
            ) : (
              <span style={{ color: '#9e9e9e' }}></span>
            )}
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={{ fontSize: 14, color: '#616161' }}>
                {canShowImport ? importPresentation.label : ''}
              </span>
              {showUpdatedAt && (
                <span style={{ fontSize: 12, color: '#9e9e9e' }}>at {importUpdatedAt}</span>
              )}
            </div>
          </div>
        )}
      </Grid>
    </GridRow>
  )
}

interface StyledTypographyProps {
  disabled?: boolean
}

const StyledTypography = styled(Typography)<StyledTypographyProps>`
  color: ${(props) => (props.disabled ? '#9e9e9e' : 'inherit')};
`

const HeaderText = styled(Typography)`
  font-weight: 600;
  color: #424242;
`

const MobileLabel = styled(Typography)`
  font-weight: 600;
  color: #757575;
  font-size: 12px;
  margin-bottom: 4px;
  display: block;

  @media (min-width: 600px) {
    display: none;
  }
`



// WEBPACK FOOTER //
// ./src/containers/Settings/Development/MigrateCompanyProducts/CollectionRow/index.tsx